import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { routes } from '@/lib/routes';
import { supervisorApi } from '@/features/workspace/api';
import { ModuleGrid, WorkspaceHero } from '@/features/workspace/components/WorkspaceShell';
import { ROLE_MODULE_THEMES } from '@/features/workspace/roleModuleThemes';
import { WorkspaceModulePage } from '@/features/workspace/WorkspaceModulePage';
import type { WorkspaceSummary } from '@/features/workspace/types';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { RoleMyRequestsOverlay } from '@/features/work-requests';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { SupervisorMeetingsOverlay } from '@/features/supervisor/components/SupervisorMeetingsOverlay';
import { SupervisionWorkspace } from '@/features/supervisor/components/SupervisionWorkspace';
import { SupervisedLearnersPage } from '@/features/supervisor/components/SupervisedLearnersPage';
import { ModerationPanel } from '@/features/supervisor/components/ModerationPanel';
import { SupervisorProjectsPage } from '@/features/supervisor/components/SupervisorProjectsPage';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

const SupervisorHome = ({ summary }: { summary: WorkspaceSummary }) => (
  <CabinetWorkspaceFrame
    links={{
      notebook: `${routes.supervisor}/files`,
      calendar: `${routes.supervisor}/meetings`,
      meetings: `${routes.supervisor}/meetings`,
      connections: `${routes.supervisor}/connections`,
      alliances: `${routes.supervisor}/connections`,
      qa: routes.portalSection('instructions'),
      support: routes.portalSection('contacts'),
    }}
  >
    <WorkspaceHero
      profile={summary.profile}
      kpis={summary.kpis}
      createRequestTo={routes.newRequest}
      assistantTo={routes.assistant}
      accent="#9e80ff"
    />
    <section className="role-cabinet">
      <ModuleGrid
        modules={summary.modules}
        basePath={routes.supervisor}
        theme={ROLE_MODULE_THEMES.supervisor}
      />
    </section>
    <ScientificGpsBlock openTo={`${routes.supervisor}/supervision`} />
  </CabinetWorkspaceFrame>
);

const SupervisorPage = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [error, setError] = useState(false);
  const backToCabinet = () => navigate(routes.supervisor);
  const canAccess = hasRole('SUPERVISOR') || hasRole('SYSTEM_OWNER');

  useEffect(() => {
    supervisorApi
      .workspace()
      .then(setSummary)
      .catch(() => setError(true));
  }, []);

  if (!canAccess) {
    return <Navigate to={routes.denied} replace />;
  }

  if (error) {
    return <Navigate to={routes.denied} replace />;
  }

  if (!summary) {
    return <section className="dashboard workspace-page" />;
  }

  return (
    <Routes>
      <Route index element={<SupervisorHome summary={summary} />} />
      <Route path="learners" element={<SupervisedLearnersPage onClose={backToCabinet} />} />
      <Route path="supervision" element={<SupervisionWorkspace onClose={backToCabinet} />} />
      <Route path="supervision/*" element={<SupervisionWorkspace onClose={backToCabinet} />} />
      <Route path="moderation" element={<ModerationPanel onClose={backToCabinet} />} />
      <Route path="moderation/*" element={<ModerationPanel onClose={backToCabinet} />} />
      <Route path="projects" element={<SupervisorProjectsPage onClose={backToCabinet} />} />
      <Route path="projects/*" element={<SupervisorProjectsPage onClose={backToCabinet} />} />
      <Route
        path="personal_requests"
        element={
          <RoleMyRequestsOverlay isOpen onClose={backToCabinet} roleContext="SUPERVISOR" />
        }
      />
      <Route
        path="connections"
        element={
          <RoleAcademicConnectionsOverlay
            isOpen
            onClose={backToCabinet}
            roleContext="SUPERVISOR"
            roleTitle="Научный руководитель"
          />
        }
      />
      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route path="meetings" element={<SupervisorMeetingsOverlay onClose={backToCabinet} />} />
      <Route path="meetings/*" element={<SupervisorMeetingsOverlay onClose={backToCabinet} />} />
      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path=":moduleKey"
        element={
          <WorkspaceModulePage
            modules={summary.modules}
            basePath={routes.supervisor}
            loadModule={(key, params) =>
              supervisorApi.module(key, {
                tab: params.tab,
                filter: params.filter,
                search: params.search || undefined,
              })
            }
          />
        }
      />
    </Routes>
  );
};

export default SupervisorPage;
